import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  LayoutDashboard, 
  Phone, 
  Users, 
  History, 
  PhoneCall,
  LogOut,
  Contact,
  Megaphone,
  Target,
  Crown,
  CreditCard,
  Sparkles,
  Shield,
  Bot,
  Link as LinkIcon,
  Settings,
  MessageSquare,
  BookUser,
  Play,
  BarChart3,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PlanUsageSidebar } from "@/components/PlanUsageWidget";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const GSK_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/aqy9mc85_2LOGO%20GSK%20.png";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/dashboard/call", icon: Phone, label: "Voice Call" },
  { path: "/dashboard/agents", icon: Users, label: "Agents" },
  { path: "/dashboard/agent-templates", icon: Sparkles, label: "Agent Templates" },
  { path: "/dashboard/agent-config", icon: Bot, label: "Configure Agents" },
  { path: "/dashboard/phonebook", icon: BookUser, label: "Phonebook", badge: "New" },
  { path: "/dashboard/campaigns-queue", icon: Play, label: "Campaign Queues", badge: "New" },
  { path: "/dashboard/performance", icon: BarChart3, label: "Performance" },
  { path: "/dashboard/scheduling", icon: Calendar, label: "Call Scheduling" },
  { path: "/dashboard/contacts", icon: Contact, label: "Contacts" },
  { path: "/dashboard/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/dashboard/leads", icon: Target, label: "Leads" },
  { path: "/dashboard/history", icon: History, label: "Call History" },
  { path: "/dashboard/phone", icon: PhoneCall, label: "Phone Setup" },
  { path: "/dashboard/phone/provision", icon: Phone, label: "Get Phone Numbers" },
  { path: "/dashboard/channels", icon: MessageSquare, label: "Channels" },
  { path: "/dashboard/billing", icon: CreditCard, label: "Billing" },
  { path: "/dashboard/integrations", icon: LinkIcon, label: "Integrations" },
  { path: "/dashboard/settings", icon: Settings, label: "Settings" },
  { path: "/dashboard/privacy", icon: Shield, label: "Privacy & Data" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case "enterprise": return "bg-gradient-to-r from-amber-500 to-orange-500 text-white";
      case "professional": return "bg-gradient-to-r from-purple-500 to-pink-500 text-white";
      case "starter": return "bg-gradient-to-r from-blue-500 to-cyan-500 text-white";
      default: return "bg-emerald-500/20 text-emerald-400";
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      <aside className="w-72 border-r border-border bg-card/50 flex flex-col" data-testid="sidebar">
        <div className="p-4 border-b border-border">
          <img 
            src={SPEAKR_LOGO} 
            alt="Speakr" 
            className="w-full h-auto max-h-20 object-contain cursor-pointer"
            onClick={() => navigate("/dashboard")}
          />
        </div>

        {user && (
          <div className="p-4 border-b border-border">
            <p className="font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        )}

        {/* Plan Usage Widget */}
        <div className="p-4 border-b border-border">
          <PlanUsageSidebar />
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/dashboard"}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  "hover:bg-secondary/80",
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium flex-1">{item.label}</span>
              {item.badge && (
                <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0">{item.badge}</Badge>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
          
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border">
            <img 
              src={GSK_LOGO} 
              alt="GSK Productions Inc." 
              className="w-9 h-9 rounded-full object-cover"
            />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Developed by</p>
              <p className="text-sm font-medium">GSK Productions Inc.</p>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
